import { API_BASE_URL } from "@/lib/api";
import type {
  PagedResult,
  CustomRequestListItem,
  CustomRequestDetail,
  GetCustomRequestsParams,
  RequestInformationRequest,
  AssignVehicleRequest,
  RejectCustomRequestRequest,
  WithdrawQuoteRequest,
  CreateQuoteRequest,
} from "@/types/admin/transport-custom-request-type";

function authHeaders(): HeadersInit {
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("adminAccessToken") || ""
      : "";
  return {
    Accept: "application/json, text/plain, */*",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handle<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    let message = fallback;
    try {
      const errData = await res.json();
      message = errData?.title || errData?.message || errData?.error || fallback;
    } catch {
      // response wasn't JSON — keep fallback
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export const transportCustomRequestsService = {
  /** GET /api/admin/transport/custom-requests */
  async getRequests(
    params: GetCustomRequestsParams,
  ): Promise<PagedResult<CustomRequestListItem>> {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    if (params.assignedPartnerId)
      query.set("assignedPartnerId", String(params.assignedPartnerId));
    if (params.assignedVehicleUid)
      query.set("assignedVehicleUid", params.assignedVehicleUid);
    if (params.pickupFrom) query.set("pickupFrom", params.pickupFrom);
    if (params.pickupTo) query.set("pickupTo", params.pickupTo);
    query.set("page", String(params.page ?? 1));
    query.set("pageSize", String(params.pageSize ?? 20));

    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests?${query.toString()}`,
      { method: "GET", headers: authHeaders() },
    );
    return handle<PagedResult<CustomRequestListItem>>(
      res,
      "Failed to load custom requests.",
    );
  },

  /** GET /api/admin/transport/custom-requests/{requestUid} */
  async getRequestById(requestUid: string): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}`,
      { method: "GET", headers: authHeaders() },
    );
    return handle<CustomRequestDetail>(res, "Failed to load request details.");
  },

  /** PATCH /api/admin/transport/custom-requests/{requestUid}/request-information */
  async requestInformation(
    requestUid: string,
    payload: RequestInformationRequest,
  ): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}/request-information`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<CustomRequestDetail>(res, "Failed to request information.");
  },

  /** PATCH /api/admin/transport/custom-requests/{requestUid}/assign-vehicle */
  async assignVehicle(
    requestUid: string,
    payload: AssignVehicleRequest,
  ): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}/assign-vehicle`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<CustomRequestDetail>(res, "Failed to assign vehicle.");
  },

  /** POST /api/admin/transport/custom-requests/{requestUid}/quotes */
  async createQuote(
    requestUid: string,
    payload: CreateQuoteRequest,
  ): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}/quotes`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<CustomRequestDetail>(res, "Failed to create quote.");
  },

  /** PUT /api/admin/transport/custom-requests/{requestUid}/quotes/{quoteUid} */
  async updateQuote(
    requestUid: string,
    quoteUid: string,
    payload: CreateQuoteRequest,
  ): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}/quotes/${quoteUid}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<CustomRequestDetail>(res, "Failed to update quote.");
  },

  /** PATCH /api/admin/transport/custom-requests/{requestUid}/quotes/{quoteUid}/withdraw */
  async withdrawQuote(
    requestUid: string,
    quoteUid: string,
    payload: WithdrawQuoteRequest,
  ): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}/quotes/${quoteUid}/withdraw`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<CustomRequestDetail>(res, "Failed to withdraw quote.");
  },

  /** PATCH /api/admin/transport/custom-requests/{requestUid}/reject */
  async rejectRequest(
    requestUid: string,
    payload: RejectCustomRequestRequest,
  ): Promise<CustomRequestDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/custom-requests/${requestUid}/reject`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<CustomRequestDetail>(res, "Failed to reject request.");
  },
};